import OverlayPanel from '@/components/shared/OverlayPanel'
import type { WebsiteContent } from '../content'

type Industry = WebsiteContent['industries']['groups'][number]

type IndustryDetailPanelProps = {
  industry: Industry | null
  isOpen: boolean
  onClose: () => void
}

type HighlightItem = {
  title: string
  description?: string
}

const splitHighlight = (text: string): HighlightItem => {
  const [title, ...rest] = text.split(' - ')
  const description = rest.join(' - ')
  return description ? { title, description } : { title }
}

function IndustryDetailPanel({ industry, isOpen, onClose }: IndustryDetailPanelProps) {
  if (!industry) return null

  const highlights = industry.highlights.map(splitHighlight)
  const exampleTitle = industry.exampleTitle?.trim()
  const exampleDescription = industry.exampleDescription?.trim()
  const exampleMedia = industry.exampleMedia

  return (
    <OverlayPanel
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={`${industry.name} details`}
      backgroundImage={industry.image}
      contentClassName="overflow-y-auto overscroll-contain pt-[clamp(24px,2.5vw,32px)] pb-[clamp(24px,5vw,72px)]"
    >
      <div className="relative flex min-h-full w-full flex-col gap-[clamp(1.5rem,3vw,2.5rem)]">
        <h2 className="pr-[clamp(88px,12vw,176px)] font-agency text-[clamp(3rem,10vw,11.25rem)] leading-[0.95] tracking-[1px] uppercase text-white md:tracking-[1.8px]">
          {industry.name.toUpperCase()}
        </h2>

        <div className="grid gap-[clamp(2rem,4vw,3rem)] lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:items-start lg:gap-[clamp(2rem,4vw,4rem)]">
          <div className="flex flex-col gap-[clamp(2rem,4vw,4rem)] text-white md:max-w-[min(680px,100%)]">
            <p className="text-[clamp(1.5rem,3vw,2.75rem)] leading-[clamp(2rem,4.2vw,3.75rem)] font-archivo font-extralight text-white">
              {industry.description}
            </p>

            <div className="space-y-[clamp(1rem,2.4vw,1.5rem)]">
              {highlights.map((item) => (
                <div key={item.title} className="space-y-2">
                  <h3 className="text-[clamp(1.5rem,2.4vw,2rem)] leading-[clamp(2rem,3vw,2.875rem)] font-archivo font-extralight text-white">
                    {item.title}
                  </h3>
                  {item.description ? (
                    <p className="text-[clamp(1rem,1.6vw,1.125rem)] leading-[clamp(1.6rem,2.4vw,2rem)] font-archivo font-light text-white">
                      {item.description}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col text-white lg:pt-[clamp(0.75rem,2vw,1.5rem)]">
            {exampleMedia ? (
              <div className="relative w-full max-w-[min(684px,100%)] overflow-hidden rounded-none bg-white/10">
                <div className="relative aspect-[684/385] w-full">
                  {exampleMedia.type === 'video' ? (
                    <video
                      className="absolute inset-0 size-full object-cover"
                      src={exampleMedia.src}
                      poster={exampleMedia.poster}
                      controls
                    />
                  ) : (
                    <img
                      className="absolute inset-0 size-full object-cover"
                      src={exampleMedia.src}
                      alt={exampleMedia.alt ?? ''}
                    />
                  )}

                  {exampleMedia.type === 'video' ? (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <div className="flex size-[clamp(44px,5vw,56px)] items-center justify-center rounded-full bg-white/75 text-gray-dark">
                        <svg aria-hidden="true" viewBox="0 0 24 24" className="size-[20px]">
                          <path d="M8 5v14l11-7z" fill="currentColor" />
                        </svg>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {exampleTitle || exampleDescription ? (
              <div className="mt-6 space-y-2">
                {exampleTitle ? (
                  <h3 className="text-[clamp(1.5rem,2.4vw,2rem)] leading-[clamp(2rem,3vw,2.875rem)] font-archivo font-extralight text-white">
                    {exampleTitle}
                  </h3>
                ) : null}
                {exampleDescription ? (
                  <p className="text-[clamp(1rem,1.6vw,1.125rem)] leading-[clamp(1.6rem,2.4vw,2rem)] font-archivo font-light text-white">
                    {exampleDescription}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </OverlayPanel>
  )
}

export default IndustryDetailPanel
